import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import ProgressBar from './ProgressBar';
import {
    Leaf,
    Users,
    Wifi,
    Coffee,
    Wind,
    Droplets,
    Sparkles,
    ChevronLeft,
    ChevronRight,
    Calendar,
    Search as SearchIcon,
    Bed,
    Maximize,
    Star
} from 'lucide-react';

// Import room images from Rooms.jsx
import suiteImg from '../assets/Resort/62.jpeg';
import deluxeImg from '../assets/Resort/33.jpeg';
import seaViewImg from '../assets/Resort/84.jpeg';
import heroBg from '../assets/Resort/111.jpeg';

// Room data with single/double pricing
const initialRooms = [
    {
        id: 1,
        name: "Luxury Suite(Sea View)",
        category: "Luxury Suite(Sea View)",
        description: "Experience ultimate serenity in our spacious Luxury Suite, designed with traditional Ayurvedic principles and breathtaking Sea views.",
        image: suiteImg,
        singlePrice: 170,
        doublePrice: 320,
        size: "75 sqm",
        occupancy: 2,
        amenities: ["King Size Bed", "Private Balcony", "Rain Shower", "Air Conditioning"],
        features: ["Garden View", "Sea View"],
        services: [
            { icon: 'Bed', text: 'King Size Bed' },
            { icon: 'Maximize', text: 'Private Balcony' },
            { icon: 'Droplets', text: 'Rain Shower' },
            { icon: 'Wind', text: 'Air Conditioning' },
            { icon: 'Wifi', text: 'High Speed WiFi' },
            { icon: 'Coffee', text: 'Tea Station' }
        ]
    },
    {
        id: 2,
        name: "Deluxe Room",
        category: "Deluxe Rooms",
        description: "Harmoniously balanced room featuring natural wood elements and cooling water features for perfect tranquility.",
        image: deluxeImg,
        singlePrice: 150,
        doublePrice: 280,
        size: "55 sqm",
        occupancy: 2,
        amenities: ["King Size Bed", "Work Desk", "Rain Shower", "Tea Station"],
        features: ["Pool View", "Natural Lighting", "Garden View"],
        services: [
            { icon: 'Bed', text: 'King Size Bed' },
            { icon: 'Maximize', text: 'Work Desk' },
            { icon: 'Droplets', text: 'Rain Shower' },
            { icon: 'Coffee', text: 'Tea Station' },
            { icon: 'Wifi', text: 'High Speed WiFi' },
            { icon: 'Wind', text: 'Climate Control' }
        ]
    },
    {
        id: 3,
        name: "Sea View Deluxe",
        category: "Deluxe Room with Sea View",
        description: "Wake up to calming sea breezes and panoramic ocean views in this elegantly designed deluxe room.",
        image: seaViewImg,
        singlePrice: 160,
        doublePrice: 290,
        size: "45 sqm",
        occupancy: 2,
        amenities: ["King Size Bed", "Private Terrace", "Rain Shower", "Binoculars"],
        features: ["Sea View", "Sunrise Balcony", "Sound of Waves"],
        services: [
            { icon: 'Bed', text: 'King Size Bed' },
            { icon: 'Maximize', text: 'Private Terrace' },
            { icon: 'Droplets', text: 'Rain Shower' },
            { icon: 'Sparkles', text: 'Binoculars' },
            { icon: 'Wifi', text: 'High Speed WiFi' },
            { icon: 'Coffee', text: 'Premium Amenities' }
        ]
    }
];

// Icon mapping component
const ServiceIcon = ({ iconName }) => {
    const iconMap = {
        'Bed': Bed,
        'Maximize': Maximize,
        'Droplets': Droplets,
        'Wind': Wind,
        'Wifi': Wifi,
        'Coffee': Coffee,
        'Sparkles': Sparkles
    };

    const IconComponent = iconMap[iconName] || Sparkles;
    return <IconComponent className="w-4 h-4" />;
};

function Search() {
    const location = useLocation();

    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Get today's date and 3 days from now (minimum stay) in YYYY-MM-DD format
    const today = new Date();
    const minimumCheckout = new Date();
    minimumCheckout.setDate(today.getDate() + 3);

    const [checkInDate, setCheckInDate] = useState(formatDate(today));
    const [checkOutDate, setCheckOutDate] = useState(formatDate(minimumCheckout));
    const [sortOrder, setSortOrder] = useState('default');
    const [priceRange, setPriceRange] = useState([150, 320]);
    const [currentPage, setCurrentPage] = useState(1);
    const [guests, setGuests] = useState(1);
    const [displayedRooms, setDisplayedRooms] = useState(initialRooms);

    const roomsPerPage = 6;

    const filterRooms = useCallback(() => {
        const startDate = new Date(checkInDate);
        const endDate = new Date(checkOutDate);
        const numberOfDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

        // Enforce minimum 3 nights stay
        if (numberOfDays < 3) {
            setDisplayedRooms([]);
            return;
        }

        const updatedRooms = initialRooms
            .map(room => {
                // Calculate price based on number of guests
                const pricePerNight = guests === 1 ? room.singlePrice : room.doublePrice;
                return {
                    ...room,
                    price: pricePerNight,
                    totalPrice: pricePerNight * numberOfDays,
                    checkIn: startDate,
                    checkOut: endDate,
                    nights: numberOfDays
                };
            })
            .filter(room =>
                room.price >= priceRange[0] &&
                room.price <= priceRange[1] &&
                room.occupancy >= guests
            );

        const sortedRooms = updatedRooms.sort((a, b) => {
            if (sortOrder === 'lowest') return a.totalPrice - b.totalPrice;
            if (sortOrder === 'highest') return b.totalPrice - a.totalPrice;
            return a.id - b.id;
        });

        setDisplayedRooms(sortedRooms);
    }, [checkInDate, checkOutDate, guests, priceRange, sortOrder]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const queryCheckIn = params.get('checkIn');
        const queryCheckOut = params.get('checkOut');
        const isValidDate = (value) => {
            if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
            return !Number.isNaN(new Date(value).getTime());
        };

        const todayForEffect = new Date();
        const effectiveCheckIn = isValidDate(queryCheckIn) ? new Date(queryCheckIn) : todayForEffect;
        const minCheckOut = new Date(effectiveCheckIn);
        minCheckOut.setDate(minCheckOut.getDate() + 3);

        setCheckInDate(formatDate(effectiveCheckIn));

        if (isValidDate(queryCheckOut)) {
            const queryCheckOutDate = new Date(queryCheckOut);
            if (queryCheckOutDate >= minCheckOut) {
                setCheckOutDate(formatDate(queryCheckOutDate));
                return;
            }
        }

        setCheckOutDate(formatDate(minCheckOut));
    }, [location.search]);

    useEffect(() => {
        filterRooms();
    }, [filterRooms]);

    const handleSortChange = (e) => {
        setSortOrder(e.target.value);
    };

    const handlePriceChange = (e, type) => {
        const value = parseInt(e.target.value);
        if (type === 'min') {
            setPriceRange([value, priceRange[1]]);
        } else {
            setPriceRange([priceRange[0], value]);
        }
    };

    const handleGuestsChange = (e) => {
        setGuests(parseInt(e.target.value, 10));
    };

    const handleCheckInChange = (e) => {
        const newCheckIn = new Date(e.target.value);
        const currentCheckOut = new Date(checkOutDate);
        const minCheckOut = new Date(newCheckIn);
        minCheckOut.setDate(minCheckOut.getDate() + 3);

        setCheckInDate(e.target.value);

        // Ensure checkout is at least 3 nights after check-in
        if (currentCheckOut < minCheckOut) {
            setCheckOutDate(formatDate(minCheckOut));
        }
    };

    const handleCheckOutChange = (e) => {
        const newCheckOut = new Date(e.target.value);
        const currentCheckIn = new Date(checkInDate);
        const minCheckOut = new Date(currentCheckIn);
        minCheckOut.setDate(minCheckOut.getDate() + 3);

        // Ensure minimum 3 nights stay
        if (newCheckOut >= minCheckOut) {
            setCheckOutDate(e.target.value);
        }
    };

    const handleSearch = () => {
        filterRooms();
        setCurrentPage(1);
    };

    const paginatedRooms = displayedRooms.slice(
        (currentPage - 1) * roomsPerPage,
        currentPage * roomsPerPage
    );

    const totalPages = Math.ceil(displayedRooms.length / roomsPerPage);

    const detailPath = (room) => {
        const category = (room.category || '').toLowerCase();
        const name = (room.name || '').toLowerCase();

        if (category.includes('luxury suite') || name.includes('luxury suite')) {
            return '/beach/rooms/luxury-suite';
        }
        if (category.includes('sea view') || name.includes('sea view')) {
            return '/beach/rooms/deluxe-sea-view';
        }
        if (category.includes('deluxe')) {
            return '/beach/rooms/deluxe';
        }
        return '/beach/rooms';
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-green-50">
            <Navbar />


            {/* Hero Section */}
            <div className="relative h-[40vh] md:h-[50vh] lg:h-[60vh] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${heroBg})` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-green-900/80 via-green-800/70 to-transparent" />
                </div>

                <div className="relative h-full flex items-center">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                        <div className="max-w-2xl">
                            <div className="flex items-center space-x-2 mb-4">
                                <Leaf className="w-6 h-6 md:w-8 md:h-8 text-amber-400" />
                                <span className="text-amber-300 tracking-widest text-xs md:text-sm font-light">
                                    MUTHUMUNI AYURVEDA RESORTS
                                </span>
                            </div>
                            <h1 className="text-3xl md:text-5xl lg:text-6xl font-light text-white mb-4 leading-tight">
                                Find Your Perfect<br />Retreat
                            </h1>
                            <p className="text-base md:text-lg lg:text-xl text-green-50 font-light leading-relaxed">
                                Discover serene accommodations crafted for your wellness journey.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <ProgressBar currentStep="search" />

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                    {/* Filter Sidebar */}
                    <div className="w-full lg:w-80 xl:w-96">
                        <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-4">
                            <h2 className="text-2xl font-light text-gray-900 mb-6 flex items-center">
                                <SearchIcon className="w-6 h-6 mr-2 text-green-700" />
                                Search Rooms
                            </h2>

                            {/* Check In Date */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Check In
                                </label>
                                <div className="relative">
                                    <input
                                        type="date"
                                        value={checkInDate}
                                        min={formatDate(new Date())}
                                        onChange={handleCheckInChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg hover:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                                    />
                                    <Calendar className="absolute right-3 top-3 w-5 h-5 text-gray-400 pointer-events-none" />
                                </div>
                            </div>

                            {/* Check Out Date */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Check Out
                                </label>
                                <div className="relative">
                                    <input
                                        type="date"
                                        value={checkOutDate}
                                        min={(() => {
                                            const minDate = new Date(checkInDate);
                                            minDate.setDate(minDate.getDate() + 3);
                                            return formatDate(minDate);
                                        })()}
                                        onChange={handleCheckOutChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg hover:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                                    />
                                    <Calendar className="absolute right-3 top-3 w-5 h-5 text-gray-400 pointer-events-none" />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">Minimum 3 nights from check-in</p>
                            </div>

                            {/* Guests */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Number of Guests
                                </label>
                                <div className="relative">
                                    <select
                                        value={guests}
                                        onChange={handleGuestsChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors appearance-none bg-white"
                                    >
                                        <option value="1">1 Guest (Single Occupancy)</option>
                                        <option value="2">2 Guests (Double Occupancy)</option>
                                    </select>
                                    <Users className="absolute right-3 top-3 w-5 h-5 text-gray-400 pointer-events-none" />
                                </div>
                                <p className="text-xs text-gray-500 mt-2">Maximum 2 guests per room</p>
                            </div>

                            {/* Price Range */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Price Range (per night)
                                </label>
                                <div className="space-y-4">
                                    <div className="flex items-center gap-4">
                                        <div className="flex-1">
                                            <label className="text-xs text-gray-500 mb-1 block">Min</label>
                                            <input
                                                type="range"
                                                min="150"
                                                max="320"
                                                step="10"
                                                value={priceRange[0]}
                                                onChange={(e) => handlePriceChange(e, 'min')}
                                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-700"
                                            />
                                            <div className="text-center text-sm font-semibold text-green-700 mt-1">
                                                €{priceRange[0]}
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <label className="text-xs text-gray-500 mb-1 block">Max</label>
                                            <input
                                                type="range"
                                                min="150"
                                                max="320"
                                                step="10"
                                                value={priceRange[1]}
                                                onChange={(e) => handlePriceChange(e, 'max')}
                                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-700"
                                            />
                                            <div className="text-center text-sm font-semibold text-green-700 mt-1">
                                                €{priceRange[1]}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Search Button */}
                            <button
                                onClick={handleSearch}
                                className="w-full bg-green-700 text-white py-3 rounded-lg font-semibold hover:bg-green-800 transition-all shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
                            >
                                <SearchIcon className="w-5 h-5" />
                                <span>Search Rooms</span>
                            </button>

                            {/* Minimum Stay Notice */}
                            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-xs text-blue-800 text-center">
                                    ℹ️ Minimum stay: <span className="font-semibold">3 nights</span>
                                </p>
                            </div>

                            {/* Results Count */}
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <p className="text-sm text-gray-600 text-center">
                                    <span className="font-semibold text-green-700">{displayedRooms.length}</span> rooms available
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Room List */}
                    <div className="flex-1">
                        {/* Sort */}
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                            <h2 className="text-2xl md:text-3xl font-light text-gray-900">
                                Available Rooms
                            </h2>
                            <div className="flex items-center space-x-2">
                                <label className="text-sm text-gray-600">Sort by:</label>
                                <select
                                    value={sortOrder}
                                    onChange={handleSortChange}
                                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                                >
                                    <option value="default">Default</option>
                                    <option value="lowest">Lowest Price</option>
                                    <option value="highest">Highest Price</option>
                                </select>
                            </div>
                        </div>

                        {/* Room Cards */}
                        {paginatedRooms.length === 0 ? (
                            <div className="text-center py-20 bg-white rounded-2xl shadow-lg">
                                <Leaf className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-xl text-gray-500">No rooms match your criteria</p>
                                <p className="text-gray-400 mt-2">Try adjusting your filters</p>
                                <p className="text-sm text-blue-600 mt-4 font-medium">✓ Minimum stay requirement: 3 nights</p>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {paginatedRooms.map((room) => (
                                    <motion.div
                                        key={room.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4 }}
                                        className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300"
                                    >
                                        <div className="flex flex-col md:flex-row">
                                            {/* Room Image */}
                                            <div className="md:w-2/5 lg:w-1/3 h-64 md:h-auto">
                                                <img
                                                    src={room.image}
                                                    alt={room.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>

                                            {/* Room Details */}
                                            <div className="flex-1 p-6 lg:p-8">
                                                <div className="flex flex-col h-full">
                                                    {/* Header */}
                                                    <div className="flex-1">
                                                        <div className="flex justify-between items-start mb-2">
                                                            <div>
                                                                <span className="inline-block px-3 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full mb-2">
                                                                    {room.category}
                                                                </span>
                                                                <h3 className="text-2xl font-light text-gray-900">
                                                                    {room.name}
                                                                </h3>
                                                            </div>
                                                            <div className="text-right bg-green-50 px-4 py-2 rounded-lg">
                                                                <div className="text-2xl font-bold text-green-700">
                                                                    €{room.price}
                                                                </div>
                                                                <div className="text-xs text-gray-600">{guests === 1 ? 'Single' : 'Double'}</div>
                                                                <div className="text-xs text-gray-500">per night</div>
                                                            </div>
                                                        </div>

                                                        <p className="text-gray-600 mb-4 leading-relaxed">
                                                            {room.description}
                                                        </p>

                                                        {/* Room Info */}
                                                        <div className="flex flex-wrap gap-4 mb-4">
                                                            <div className="flex items-center space-x-2 text-sm text-gray-700">
                                                                <Maximize className="w-4 h-4 text-green-600" />
                                                                <span>{room.size}</span>
                                                            </div>
                                                            <div className="flex items-center space-x-2 text-sm text-gray-700">
                                                                <Users className="w-4 h-4 text-green-600" />
                                                                <span>Up to {room.occupancy} guests</span>
                                                            </div>
                                                            <div className="flex items-center space-x-2 text-sm text-gray-700">
                                                                <Star className="w-4 h-4 text-amber-500" />
                                                                <span>{room.nights} nights</span>
                                                            </div>
                                                        </div>

                                                        {/* Amenities Icons */}
                                                        <div className="flex flex-wrap gap-3 mb-4">
                                                            {room.services.slice(0, 6).map((service, index) => (
                                                                <div
                                                                    key={index}
                                                                    className="group relative flex items-center justify-center w-10 h-10 bg-green-50 rounded-full hover:bg-green-100 transition-colors"
                                                                >
                                                                    <ServiceIcon iconName={service.icon} />
                                                                    <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                                                                        {service.text}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>

                                                        {/* Total Price */}
                                                        <div className="bg-gradient-to-r from-amber-50 to-green-50 rounded-lg p-4 mb-4 border border-amber-200">
                                                            <div className="flex justify-between items-center">
                                                                <div>
                                                                    <span className="text-sm text-gray-600 block">
                                                                        Total for {room.nights} night{room.nights > 1 ? 's' : ''}
                                                                    </span>
                                                                    <span className="text-xs text-gray-500">
                                                                        {guests === 1 ? 'Single' : 'Double'} occupancy
                                                                    </span>
                                                                </div>
                                                                <span className="text-2xl font-bold text-green-700">
                                                                    €{room.totalPrice?.toFixed(2)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Buttons */}
                                                    <div className="flex flex-col sm:flex-row gap-3">
                                                        <Link
                                                            to={detailPath(room)}
                                                            className="flex-1 text-center border-2 border-green-700 text-green-700 px-6 py-3 rounded-lg font-medium hover:bg-green-50 transition-all"
                                                        >
                                                            View Details
                                                        </Link>
                                                        <Link
                                                            to="/beach/booking"
                                                            state={{
                                                                room: {
                                                                    id: room.id,
                                                                    name: room.name,
                                                                    price: room.price,
                                                                    totalPrice: room.totalPrice,
                                                                    image: room.image,
                                                                    checkIn: room.checkIn,
                                                                    checkOut: room.checkOut,
                                                                    nights: room.nights,
                                                                    services: room.services
                                                                },
                                                                guests: guests
                                                            }}
                                                            className="flex-1 text-center bg-green-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-800 transition-all shadow-md hover:shadow-lg"
                                                        >
                                                            Book Now
                                                        </Link>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex justify-center items-center space-x-2 mt-8">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>

                                {[...Array(totalPages)].map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setCurrentPage(index + 1)}
                                        className={`px-4 py-2 rounded-lg font-medium transition-all ${currentPage === index + 1
                                            ? 'bg-green-700 text-white shadow-md'
                                            : 'border border-gray-300 hover:bg-green-50 text-gray-700'
                                            }`}
                                    >
                                        {index + 1}
                                    </button>
                                ))}

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="relative py-16 md:py-24 overflow-hidden mt-12">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: "url('https://images.pexels.com/photos/3771640/pexels-photo-3771640.jpeg?auto=compress&cs=tinysrgb&w=1600')",
                    }}
                >
                    <div className="absolute inset-0 bg-green-900/85"></div>
                </div>

                <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Leaf className="w-10 h-10 md:w-12 md:h-12 text-amber-400 mx-auto mb-6" />
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-6">
                        Need Help Choosing?
                    </h2>
                    <p className="text-lg md:text-xl text-green-100 mb-8 md:mb-10 font-light leading-relaxed max-w-2xl mx-auto">
                        Our wellness advisors are here to help you find the perfect accommodation for your healing journey.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                        <Link
                            to="/beach/contact-us"
                            className="w-full sm:w-auto bg-white text-green-800 px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold hover:bg-amber-50 transition-all duration-300 shadow-xl hover:shadow-2xl"
                        >
                            Contact Us
                        </Link>
                        <Link
                            to="/beach/rooms"
                            className="w-full sm:w-auto border-2 border-white text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold hover:bg-white/10 transition-all duration-300"
                        >
                            View Rooms
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Search;
